/*
 * This file contains utility functions for date calculations, formatting, and mapping.
 */

// Function to calculate the difference in days between two dates
export const getDateDifference = (date) => {
  let today = new Date();
  today.setHours(0, 0, 0, 0);
  let formatted_date = new Date(date);

  const diffTime = new Date(formatted_date) - new Date(today);
  const date_diff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return date_diff;
};

// This function determines the availability date based on the provided date and unit status.
// If the date difference is less than or equal to 0 and the unit status is "Vacant Ready (Available)", it returns "Now".
// Otherwise, it formats the provided date in the format MM/DD/YYYY.
export const getAvailabilityDate = (date, unitStatus) => {
  if (date === "0000-00-00" || date === null) {
    return "";
  }
  
  let date_diff = getDateDifference(date);

  if (date_diff <= 0 && unitStatus === "Vacant Ready (Available)") {
    return "Now";
  } else {
    return `${date?.split("-")[1]}/${date?.split("-")[2]}/${
      date?.split("-")[0]
    }`;
  }
};

// This function determines the move-in date based on the provided date and vacancy class.
// If the date difference is less than or equal to 0 and the vacancy class is "2", it returns the current date.
// Otherwise, it formats the provided date in the format MM/DD/YYYY.
export const getMoveinDate = (date, vacancyclass) => {
  let date_diff = getDateDifference(date);

  if (date_diff <= 0 && vacancyclass === "2") {
    return new Date().toLocaleDateString("en-US");
  } else {
    return `${date?.split("-")[1]}/${date?.split("-")[2]}/${
      date?.split("-")[0]
    }`;
  }
};

// This function formats a given value as a currency in USD, with two decimal places if the value is not an integer.
export const formatCurrency = (value) => {
  const hasDecimals = value % 1 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(value);
};

// This function maps a floor number to its corresponding ordinal suffix (st, nd, rd, or th).
export const floorMapper = (floor) => {
  const floorString = floor.toString();
  const floordictionary = { 1: "st", 2: "nd", 3: "rd", ground: "" };
  if (!!floordictionary[floorString[floorString.length - 1]]) {
    if (floorString[floorString.length - 2] === "1") {
      return "th";
    } else return floordictionary[floorString[floorString.length - 1]];
  } else return "th";
};

export const getSpecialsList = (specials) => {
  return Object.keys(specials?.floorplan);
};

export const getUnitIds = (fpData) => {
  return fpData?.units?.map((unit) => unit?.unit_number);
};

// This function formats a given count by padding single digit numbers with a leading zero.
export const formatResultCount = (count) => {
  const intCount = parseInt(count);

  if (intCount === 0) return "0";
  else if (intCount < 10) return `0${intCount.toString()}`;
  else return intCount.toString();
};

// Helper function to append parameters to URL
export const appendUrlParams = (baseUrl, ...paramSets) => {
  const validParams = paramSets.filter(Boolean);
  if (validParams.length === 0) return baseUrl;
  
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${validParams.join('&')}`;
};

// Helper function to construct breadcrumb URL with page params from localStorage
export const getBreadcrumbUrl = (params) => {
  const storedPage = localStorage.getItem('floorplanCurrentPage');
  const baseUrl = `/find-your-home/floorplans`;
  
  // Set a flag in sessionStorage to indicate this is breadcrumb navigation
  sessionStorage.setItem('isBreadcrumbNavigation', 'true');
  
  const pageParam = storedPage ? `page=${storedPage}` : null;
  return appendUrlParams(baseUrl, params, pageParam);
};

// Helper function to construct unit listing breadcrumb URL with unit listing params
export const getUnitListingBreadcrumbUrl = (floorplanName, floorplanId, params) => {
  const storedParams = localStorage.getItem('floorplanUnitListingParams');
  const storedPage = localStorage.getItem('floorplanUnitCurrentPage');
  
  const baseUrl = `/find-your-home/floorplans/${floorplanName}/${floorplanId}`;
  
  // Use stored params if available, otherwise use current params with optional page
  if (storedParams) {
    return `${baseUrl}${storedParams}`;
  }
  
  const pageParam = storedPage ? `page=${storedPage}` : null;
  return appendUrlParams(baseUrl, params, pageParam);
};
